import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useLanguageStore } from '../store/languageStore';
import { usePatients } from '../hooks/usePatients';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { 
  Users, 
  FileText, 
  Plus, 
  TrendingUp,
  ArrowRight,
  Eye
} from 'lucide-react';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  delay?: number;
}> = ({ title, value, icon, color, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { isRTL } = useLanguageStore();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.4 }}
      className="card shadow-sm border-0 h-100"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="card-body position-relative overflow-hidden">
        {/* Background gradient */}
        <div className={`position-absolute top-0 start-0 w-100 h-100 bg-gradient ${color} opacity-0 opacity-25-hover transition-opacity`} />
        
        <div className={`position-relative d-flex align-items-center justify-content-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div>
            <p className="text-muted small mb-1">{title}</p>
            <motion.h3
              className="mb-0 fw-bold"
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ delay: delay + 0.2, type: 'spring', stiffness: 200 }}
            >
              {value}
            </motion.h3>
          </div>
          <motion.div
            className={`rounded p-3 ${color} shadow-sm text-white`}
            whileHover={{ rotate: 360, scale: 1.1 }}
            transition={{ duration: 0.6 }}
          >
            {icon}
          </motion.div>
        </div>
        
        <div className={`d-flex align-items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          <TrendingUp className={`text-success ${isRTL ? 'ms-2' : 'me-2'}`} size={16} />
          <span className="text-muted small">Active</span>
        </div>
      </div>
    </motion.div>
  );
};

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: patients, isLoading } = usePatients();
  const { isRTL } = useLanguageStore();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const recentPatients = patients?.slice(0, 5) || [];
  const totalPatients = patients?.length || 0;

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('dashboard.title')}
        actions={
          <div className={`d-flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button
              variant="secondary"
              onClick={() => navigate('/patients/new')}
            >
              {isRTL ? (
                <>
                  <Plus className="ms-2" size={16} />
                  {t('patients.addPatient')}
                </>
              ) : (
                <>
                  <Plus className="me-2" size={16} />
                  {t('patients.addPatient')}
                </>
              )}
            </Button>
            <Button 
              variant="gradient"
              onClick={() => navigate('/visits/new')}
            >
              {isRTL ? (
                <>
                  <Plus className="ms-2" size={16} />
                  {t('common.newVisit')}
                </>
              ) : (
                <>
                  <Plus className="me-2" size={16} />
                  {t('common.newVisit')}
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Stats Grid - Bootstrap Grid */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-md-6">
          <StatCard
            title={t('dashboard.totalPatients')}
            value={totalPatients}
            icon={<Users className="text-white" size={24} />}
            color="bg-primary"
            delay={0}
          />
        </div>
        <div className="col-12 col-md-6">
          <StatCard
            title={t('common.prescriptions')}
            value="-"
            icon={<FileText className="text-white" size={24} />}
            color="bg-info"
            delay={0.1}
          />
        </div>
      </div>

      {/* Recent Patients - Bootstrap Card */}
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.4 }}
        className="card shadow-sm border-0"
      >
        <div className="card-header bg-white border-bottom">
          <div className={`d-flex align-items-center justify-content-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            {recentPatients.length > 0 && (
              <Button
                variant="text"
                onClick={() => navigate('/patients')}
                className="text-primary"
              >
                {isRTL ? (
                  <>
                    <ArrowRight className="ms-2" size={16} />
                    {t('dashboard.viewAll')}
                  </>
                ) : (
                  <>
                    {t('dashboard.viewAll')}
                    <ArrowRight className="me-2" size={16} />
                  </>
                )}
              </Button>
            )}
            <div className={`${isRTL ? 'text-start' : 'text-end'}`}>
              <h5 className="card-title mb-0 fw-bold">{t('dashboard.recentPatients')}</h5>
              <p className="text-muted small mb-0">{t('dashboard.latestRegistered')}</p>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <LoadingSpinner />
            </div>
          ) : recentPatients.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0" dir={isRTL ? 'rtl' : 'ltr'} style={{ 
                backgroundColor: '#ffffff',
                borderRadius: '0.5rem',
                overflow: 'hidden'
              }}>
                <thead style={{ 
                  backgroundColor: isRTL ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  borderBottom: '2px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <tr>
                    <th 
                      className={isRTL ? 'pe-3' : 'ps-3'} 
                      style={{ 
                        textAlign: 'center', 
                        width: '60px',
                        fontWeight: '600',
                        color: '#1e40af',
                        padding: '1rem 0.75rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {t('common.id', 'ID')}
                    </th>
                    <th style={{ 
                      textAlign: isRTL ? 'right' : 'left',
                      fontWeight: '600',
                      color: '#1e40af',
                      padding: '1rem 0.75rem',
                      fontSize: '0.875rem'
                    }}>
                      {t('patients.patientName')}
                    </th>
                    <th style={{ 
                      textAlign: isRTL ? 'right' : 'left',
                      fontWeight: '600',
                      color: '#1e40af',
                      padding: '1rem 0.75rem',
                      fontSize: '0.875rem'
                    }}>
                      {t('patients.age')}
                    </th>
                    <th style={{ 
                      textAlign: isRTL ? 'right' : 'left',
                      fontWeight: '600',
                      color: '#1e40af',
                      padding: '1rem 0.75rem',
                      fontSize: '0.875rem'
                    }}>
                      {t('patients.phone')}
                    </th>
                    <th style={{ 
                      textAlign: isRTL ? 'right' : 'left',
                      fontWeight: '600',
                      color: '#1e40af',
                      padding: '1rem 0.75rem',
                      fontSize: '0.875rem'
                    }}>
                      {t('patients.gender')}
                    </th>
                    <th 
                      className={`text-center ${isRTL ? 'ps-3' : 'pe-3'}`}
                      style={{ 
                        fontWeight: '600',
                        color: '#1e40af',
                        padding: '1rem 0.75rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentPatients.map((patient, index) => (
                    <tr
                      key={patient.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#eff6ff';
                        e.currentTarget.style.transform = 'translateX(2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <td className={`text-center ${isRTL ? 'pe-3' : 'ps-3'}`} style={{ padding: '1rem 0.75rem' }}>
                        <span style={{ 
                          color: '#64748b',
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>
                          {patient.id}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        <div className="d-flex align-items-center gap-2">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center text-white" 
                            style={{ 
                              width: '36px', 
                              height: '36px', 
                              minWidth: '36px', 
                              flexShrink: 0,
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                            }}
                          >
                            <Users size={18} />
                          </div>
                          <div className={`flex-grow-1 ${isRTL ? 'text-end' : 'text-start'}`}>
                            <div style={{ 
                              fontWeight: '600',
                              color: '#1e293b',
                              fontSize: '0.9375rem'
                            }}>
                              {patient.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        <span style={{ 
                          color: '#64748b',
                          fontSize: '0.875rem'
                        }}>
                          {patient.age} {t('patients.years')}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        <span style={{ 
                          color: '#64748b',
                          fontSize: '0.875rem'
                        }}>
                          {patient.phone}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        {patient.gender && (
                          <span 
                            className="badge"
                            style={{
                              backgroundColor: patient.gender === 'Male' ? '#3b82f6' : '#ef4444',
                              color: '#ffffff',
                              padding: '0.375rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}
                          >
                            {patient.gender === 'Male' ? t('patients.male', 'Male') : t('patients.female', 'Female')}
                          </span>
                        )}
                      </td>
                      <td className={`text-center ${isRTL ? 'ps-3' : 'pe-3'}`} style={{ padding: '1rem 0.75rem' }}>
                        <div style={{ display: 'inline-block' }}>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/patients/${patient.id}`);
                            }}
                            className="text-primary"
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-5"
            >
              <motion.div
                className="d-inline-block p-4 rounded-circle bg-light mb-3"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: 'reverse'
                }}
              >
                <Users className="text-muted" size={48} />
              </motion.div>
              <p className="text-muted mb-4">{t('dashboard.noPatients')}</p>
              <Button
                variant="gradient"
                onClick={() => navigate('/patients/new')}
              >
                {isRTL ? (
                  <>
                    <Plus className="ms-2" size={16} />
                    {t('dashboard.addFirstPatient')}
                  </>
                ) : (
                  <>
                    <Plus className="me-2" size={16} />
                    {t('dashboard.addFirstPatient')}
                  </>
                )}
              </Button>
            </motion.div>
          )}
        </div>
      </motion.div>

    </div>
  );
};
